import type { ComponentChildren } from 'preact';
import './BrowserWindow.scss';

/**
 * The reusable "browser window" chrome — three traffic-light dots, an
 * address bar, then the body. Used in the hero (anchor for the
 * authentication demo) and inside each FeatureTouts row's visual.
 *
 * Designed to be content-agnostic: drop any children inside and they
 * sit in a uniformly-padded panel with the same blur/border treatment
 * everywhere on the page. The visual continuity is what makes the
 * different demo cards feel like the same product surface, not
 * disparate vignettes.
 */
export interface BrowserWindowProps {
	url?: string;
	children: ComponentChildren;
	/** Optional max-width override for tighter row layouts. */
	maxWidth?: string;
	/** Tiny variant trims the chrome for the smaller feature-row cards. */
	size?: 'lg' | 'md';
	/** Extra class hook for per-instance positioning/translation. */
	class?: string;
}

export function BrowserWindow(props: BrowserWindowProps) {
	const size = props.size ?? 'lg';
	return (
		<div
			class={`m-window m-window--${size} ${props.class ?? ''}`}
			style={props.maxWidth ? { maxWidth: props.maxWidth } : undefined}
		>
			<div class="m-window__bar">
				<span class="m-window__dot m-window__dot--r" />
				<span class="m-window__dot m-window__dot--y" />
				<span class="m-window__dot m-window__dot--g" />
				{props.url && <span class="m-window__addr">{props.url}</span>}
			</div>
			<div class="m-window__body">{props.children}</div>
		</div>
	);
}
